import React, { useContext, useEffect } from 'react'
import { useTheme } from '@mui/material/styles'
import AmbuNetTheme from '../../../../components/Theme'
import MandatoryTrainingTab from './MandatoryTrainingTab'
import DetailsTab from './detailsTab/Details'
import QualificationsTab from './qualificationsTab/Qualifications'

//@MUI
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import SchoolIcon from '@mui/icons-material/School'
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium'
import PaymentsIcon from '@mui/icons-material/Payments'
import FolderOpenIcon from '@mui/icons-material/FolderOpen'
import StickyNote2Icon from '@mui/icons-material/StickyNote2'
import RateReviewIcon from '@mui/icons-material/RateReview'
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety'
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart'
import { useLocation } from 'react-router-dom'
import { EmployeeContext } from './employeeSingle'
import { TenantSettingsContext } from '../../../../App'
import DocsTab from './Documents/DocsTab'
import NotesTab from './Notes/NotesTab'
import PayrollTab from './Payroll/PayrollTab'
import AppraisalsTab from './AppraisalsTab'
import OccupationalHealthTab from './OccupationalHealthTab'
import RadiationDosesTab from './RadiationDosesTab'
import Shifts from './Shifts'
import useIsMobile from '../../../../helpers/useIsMobile'

// Tab order defines the openTab indexes - the ?tab= query params below and
// any navigate(...?tab=...) callers rely on them staying stable
const TAB_CONFIG = [
  { label: 'Details', queryParam: 'details', icon: <PersonOutlineIcon /> },
  {
    label: 'Mandatory Training',
    queryParam: 'mandatorytraining',
    icon: <SchoolIcon />,
  },
  {
    label: 'Qualifications',
    queryParam: 'qualifications',
    icon: <WorkspacePremiumIcon />,
  },
  {
    label: 'Payroll & Shifts',
    queryParam: 'payroll',
    // Booked shifts used to be a permanent panel under the tabs; keep old
    // ?tab=shifts style deep links working now they live in here
    aliases: ['shifts'],
    icon: <PaymentsIcon />,
  },
  { label: 'Documents', queryParam: 'documents', icon: <FolderOpenIcon /> },
  { label: 'Notes', queryParam: 'notes', icon: <StickyNote2Icon /> },
  { label: 'Appraisals', queryParam: 'appraisals', icon: <RateReviewIcon /> },
  {
    label: 'Occupational Health',
    queryParam: 'occupationalhealth',
    icon: <HealthAndSafetyIcon />,
  },
]

const EmployeeTabs = ({
  employee,
  setManCoursesRequired,
  setManCoursesCompleted,
  loadEmployee,
}) => {
  const theme = useTheme(AmbuNetTheme)
  const isMobile = useIsMobile()
  const handleTabChange = (event, newValue) => setOpenTab(newValue)

  const { openTab, setOpenTab } = useContext(EmployeeContext)
  const imagingEnabled =
    useContext(TenantSettingsContext).orgConfig?.orgSettings
      ?.patientRecordSettings?.imagingSettings?.enabled ?? false

  // Appended last so the fixed TAB_CONFIG indexes stay stable when the
  // imaging module is off
  const tabConfig = [
    ...TAB_CONFIG,
    ...(imagingEnabled
      ? [
          {
            label: 'Radiation Doses',
            queryParam: 'radiationdoses',
            icon: <MonitorHeartIcon />,
          },
        ]
      : []),
  ]

  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const tab = queryParams.get('tab')

  useEffect(() => {
    const index = tabConfig.findIndex(
      (config) =>
        config.queryParam === tab || (config.aliases ?? []).includes(tab),
    )
    if (index > 0) setOpenTab(index)
  }, [])

  function TabPanel(props) {
    const { children, value, index, title, ...other } = props

    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`full-width-tabpanel-${index}`}
        aria-labelledby={`full-width-tab-${index}`}
        {...other}
      >
        {value === index && (
          <Box>
            <Typography variant="title3" fontWeight={700} mt={1} mb={1.5}>
              {title}
            </Typography>
            {children}
          </Box>
        )}
      </div>
    )
  }

  const tabContent = [
    <DetailsTab employeeData={employee} loadEmployee={loadEmployee} />,
    <MandatoryTrainingTab
      employee={employee}
      setManCoursesCompleted={setManCoursesCompleted}
      setManCoursesRequired={setManCoursesRequired}
    />,
    <QualificationsTab employee={employee} />,
    <Stack gap={3}>
      <PayrollTab employee={employee} loadEmployee={loadEmployee} />
      <Shifts employeeID={employee?.employee?._id} />
    </Stack>,
    <DocsTab employee={employee} />,
    <NotesTab employeeID={employee?.employee?._id} />,
    <AppraisalsTab employee={employee} loadEmployee={loadEmployee} />,
    <OccupationalHealthTab employee={employee} />,
    ...(imagingEnabled ? [<RadiationDosesTab employee={employee} />] : []),
  ]

  return (
    <>
      <Tabs
        value={openTab}
        onChange={handleTabChange}
        variant="fullWidth"
        sx={{ flexWrap: 'wrap' }}
      >
        {tabConfig.map((config) => (
          <Tab
            key={config.label}
            aria-label={config.label}
            // Desktop gets the title under the icon; phones stay icon-only
            // (with the tooltip) since the labels don't fit
            label={isMobile ? undefined : config.label}
            icon={
              isMobile ? (
                <Tooltip title={config.label} arrow>
                  {config.icon}
                </Tooltip>
              ) : (
                config.icon
              )
            }
            sx={{
              minWidth: 0,
              px: 0.5,
              fontSize: '0.7rem',
              textTransform: 'none',
              minHeight: isMobile ? undefined : 64,
            }}
          />
        ))}
      </Tabs>
      {tabConfig.map((config, index) => (
        <TabPanel
          key={config.label}
          value={openTab}
          index={index}
          title={config.label}
        >
          {tabContent[index]}
        </TabPanel>
      ))}
    </>
  )
}

export default EmployeeTabs
